import { css } from '@emotion/react';
import styled from '@emotion/styled';
import debounce from 'lodash/debounce';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { colors, NAVBAR_HEIGHT_L } from '../../../../utils/styleguide';
import { MAX_WIDTH } from '../../layout/12ColGridLayout';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const window = require('global/window');

type WrapperType = {
  backgroundColor?: string;
  top?: number;
};

const Wrapper = styled.nav`
  margin: 0 auto;
  padding: 0;
  max-width: ${MAX_WIDTH}px;
  width: 100%;
  position: fixed;
  ${({ top }: WrapperType) => `top: ${top}px;`}
  left: 0;
  right: 0;
  z-index: 1;
  transition: top 0.5s, background-color 0.5s ease;
  height: inherit;
  ${({ backgroundColor }: WrapperType) =>
    css`
      background-color: ${backgroundColor};
    `}
`;

type Props = {
  children: ({ textColor }: { textColor: string }) => ReactNode;
  preventHideNavBar?: boolean;
  hideNavBar?: () => void;
  initiallyTransparent?: boolean;
  overrideTransparent?: boolean;
  onHover?: (hovered: boolean) => void;
};

const HeaderWrapper = ({
  children,
  hideNavBar,
  preventHideNavBar,
  initiallyTransparent,
  overrideTransparent,
  onHover,
}: Props) => {
  const showNavbarRef = useRef(true);
  const smoothScrollingDoneRef = useRef(false);
  const preventHideNavBarRef = useRef(preventHideNavBar);
  const offsetRef = useRef(0);
  const [navbarTop, setNavbarTop] = useState<number>(0);
  const [hasScrollColor, setHasScrollColor] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isTransparent = initiallyTransparent && !hasScrollColor && !hovered && !overrideTransparent;
  const textColor = isTransparent ? colors.white : colors.black;
  const navBarBackgroundColor = isTransparent ? 'transparent' : colors.white;
  const scrollColorRef = useRef<number>();

  useEffect(() => {
    // Preventing to hide NavBar
    if (preventHideNavBar) {
      preventHideNavBarRef.current = true;
      showNavbarRef.current = false;
    } else {
      showNavbarRef.current = true;
      preventHideNavBarRef.current = false;
    }
  }, [preventHideNavBar]);

  function handleMouseEnter() {
    setHovered(true);
    onHover?.(true);
  }

  function handleMouseLeave() {
    setHovered(false);
    onHover?.(false);
  }

  const scrollHandler = useCallback(() => {
    if (smoothScrollingDoneRef.current) {
      smoothScrollingDoneRef.current = false;
      delete window.smoothScrollingTo;
    }
    if (window.smoothScrollingTo === window.pageYOffset) {
      smoothScrollingDoneRef.current = true;
    }

    // Show navbar if near top or scrolling upwards
    const isScrolledUp = window.pageYOffset < 5 || window.pageYOffset < offsetRef.current;
    const isScrolledDown = window.pageYOffset >= 5 && window.pageYOffset > offsetRef.current;

    if (
      (isScrolledDown && showNavbarRef.current && !preventHideNavBarRef.current) ||
      window.smoothScrollingTo
    ) {
      if (hideNavBar) {
        hideNavBar();
      }

      setNavbarTop(-NAVBAR_HEIGHT_L);
      showNavbarRef.current = false;
    } else if (isScrolledUp && !showNavbarRef.current && !window.smoothScrollingTo) {
      setNavbarTop(0);
      showNavbarRef.current = true;
    }

    const previousHasScrolled = offsetRef.current > 0;
    const hasScrolled = window.pageYOffset > 0;

    if (previousHasScrolled !== hasScrolled) {
      if (scrollColorRef.current) {
        clearTimeout(scrollColorRef.current);
      }
      if (hasScrolled) {
        // avoid brutal color change
        scrollColorRef.current = window.setTimeout(() => {
          setHasScrollColor(hasScrolled);
        }, 1000);
      } else {
        setHasScrollColor(hasScrolled);
      }
    }

    offsetRef.current = window.pageYOffset;
  }, [hideNavBar, setHasScrollColor]);

  useEffect(() => {
    const debouncedScroll = debounce(scrollHandler, 300, {
      leading: true,
      trailing: true,
      maxWait: 100,
    });
    window.addEventListener('scroll', debouncedScroll);
    debouncedScroll();
    return () => window.removeEventListener('scroll', debouncedScroll);
  }, [scrollHandler]);

  useEffect(() => {
    return () => {
      if (scrollColorRef.current) {
        clearTimeout(scrollColorRef.current);
      }
    };
  }, []);

  return (
    <Wrapper
      id="navbar"
      aria-label="Header"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      backgroundColor={navBarBackgroundColor}
      top={navbarTop}
    >
      {children({ textColor })}
    </Wrapper>
  );
};

export default HeaderWrapper;
